import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, Users, Check, ChevronRight, Edit2, X, Mail } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    guest_name: '',
    email: '',
    phone: '',
    time: '',
    guests: '',
    special_requests: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [managementToken, setManagementToken] = useState(null);

  const timeSlots = [
    '12:00', '12:15', '12:30', '12:45', '13:00', 
    '13:15', '13:30', '13:45', '14:00','14:15','14:30', '14:45', '15:00', 
    
    '17:00', '17:15', '17:30', '17:45', '18:00', 
    '18:15', '18:30', '18:45', '19:00','19:15','19:30', '19:45', '20:00', 
    
    '20:15', '20:30', '20:45', '21:00', 
    '21:15', '21:30', '21:45', '22:00','22:15','22:30', 
  ];

  const guestOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }
    
    if (!formData.time) {
      toast.error('Please select a time');
      return;
    }

    if (!formData.guests) {
      toast.error('Please select number of guests');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        guests: parseInt(formData.guests)
      });
      
      setBookingId(response.data.id);
      setManagementToken(response.data.management_token);
      setSuccess(true);
      toast.success('Reservation confirmed! Check your email for details.');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to make reservation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen pt-20 md:pt-24 bg-gradient-to-b from-silver-bright to-white" data-testid="booking-page">
        <div className="container-elegant py-16">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-gray-800 mb-6">
              Reservation Confirmed!
            </h1>
            <p className="text-gray-600 text-lg mb-4">
              Thank you, {formData.guest_name}. Your table has been reserved.
            </p>
            
            {/* Booking Details Card */}
            <div className="card-elegant p-6 text-left mb-6">
              <div className="space-y-4">
                <div className="flex justify-between border-b border-malva/10 pb-3">
                  <span className="text-gray-500">Confirmation #</span>
                  <span className="text-gray-800 font-mono font-medium">{bookingId?.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex justify-between border-b border-malva/10 pb-3">
                  <span className="text-gray-500">Date</span>
                  <span className="text-gray-800">{date && format(date, 'EEEE, MMMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between border-b border-malva/10 pb-3">
                  <span className="text-gray-500">Time</span>
                  <span className="text-gray-800">{formData.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Guests</span>
                  <span className="text-gray-800">{formData.guests} {parseInt(formData.guests) === 1 ? 'guest' : 'guests'}</span>
                </div>
              </div>
            </div>
            
            {/* Email Confirmation Notice */}
            <div className="bg-malva/10 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-malva-dark mb-2">
                <Mail className="w-5 h-5" />
                <span className="font-medium">Confirmation Email Sent</span>
              </div>
              <p className="text-gray-600 text-sm">
                We've sent a confirmation email to <strong>{formData.email}</strong> with your reservation details and a link to manage your booking.
              </p>
            </div>
            
            {/* Management Options */}
            <div className="card-elegant p-6 mb-8 bg-gradient-to-br from-white to-silver-bright">
              <h3 className="font-serif text-lg text-gray-800 mb-4">Need to Make Changes?</h3>
              <p className="text-gray-500 text-sm mb-4">
                You can modify or cancel your reservation at any time using the link below or from your confirmation email.
              </p>
              {managementToken && (
                <Link to={`/manage-booking/${managementToken}`}>
                  <Button className="w-full btn-secondary mb-3" data-testid="manage-booking-btn">
                    <Edit2 className="w-4 h-4 mr-2" />
                    Modify or Cancel Reservation
                  </Button>
                </Link>
              )}
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Change date/time
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Adjust guest count
                </span>
                <span className="flex items-center gap-1">
                  <X className="w-4 h-4 text-red-400" /> Cancel booking
                </span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  setSuccess(false);
                  setFormData({ guest_name: '', email: '', phone: '', time: '', guests: '', special_requests: '' });
                  setDate(null);
                  setManagementToken(null);
                }}
                className="btn-secondary"
                data-testid="make-another-reservation"
              >
                Make Another Reservation
              </Button>
              <Link to="/">
                <Button variant="outline" className="w-full sm:w-auto">
                  Return to Home
                </Button>
              </Link>
            </div>
            
            {/* Contact Info */}
            <p className="text-gray-400 text-sm mt-8">
              Questions? Contact us at{' '}
              <a href="mailto:bookings@revadining.co.uk" className="text-malva-dark hover:underline">
                bookings@revadining.co.uk
              </a>{' '}
              or call{' '}
              <a href="tel:+442088929841" className="text-malva-dark hover:underline">
                +44 20 8892 9841
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 md:pt-24 bg-gradient-to-b from-silver-bright to-white" data-testid="booking-page">
      {/* Hero Banner */}
      <section className="page-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1773100699991-b7e6bf89a6a1?w=1920&q=80"
            alt="Reserve Your Table"
            className="w-full h-full object-cover"
          />
          <div className="page-hero-overlay" />
        </div>
        <div className="page-hero-content">
          <div className="max-w-3xl">
            <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-4">
              Reservations
            </p>
            <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
              Reserve Your Table
            </h1>
            <p className="text-white/80 text-lg">
              Join us for an unforgettable dining experience. We recommend booking 
              in advance to ensure availability.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding">
        <div className="container-elegant">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <div className="card-elegant p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-silver-bright/50 border-malva/20 hover:bg-malva/10 text-gray-800"
                          data-testid="date-picker-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-malva-dark" />
                          {date ? format(date, 'PPP') : <span className="text-gray-400">Select date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white border-malva/20" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          data-testid="date-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-gray-400">Every Day Open</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700">Time</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-malva-dark pointer-events-none" />
                      <select
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 bg-silver-bright/50 border border-malva/20 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-malva/30 focus:border-malva appearance-none cursor-pointer"
                        data-testid="time-select"
                        required
                      >
                        <option value="" disabled>Select time</option>
                        <optgroup label="Lunch (12:00 PM - 3:00 PM)">
                          {timeSlots.filter(t => t >= '12:00' && t <= '15:00').map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Dinner (5:00 PM - 10:30 PM)">
                          {timeSlots.filter(t => t >= '17:00').map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </optgroup>
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">Lunch & Dinner available</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700">Number of Guests</Label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-malva-dark pointer-events-none" />
                      <select
                        value={formData.guests}
                        onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 bg-silver-bright/50 border border-malva/20 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-malva/30 focus:border-malva appearance-none cursor-pointer"
                        data-testid="guests-select"
                        required
                      >
                        <option value="" disabled>Select guests</option>
                        {guestOptions.map((num) => (
                          <option key={num} value={num.toString()}>
                            {num} {num === 1 ? 'Guest' : 'Guests'}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700">Phone</Label>
                    <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange}
                      placeholder="+44 20 8892 9841" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="phone-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="guest_name" className="text-gray-700">Full Name</Label>
                  <Input id="guest_name" name="guest_name" type="text" required value={formData.guest_name} onChange={handleChange}
                    placeholder="John Smith" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="guest-name-input" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">Email</Label>
                  <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange}
                    placeholder="john@example.com" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="email-input" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="special_requests" className="text-gray-700">Special Requests (Optional)</Label>
                  <Textarea id="special_requests" name="special_requests" value={formData.special_requests} onChange={handleChange}
                    placeholder="Allergies, celebrations, seating preferences..." rows={4}
                    className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400 resize-none" data-testid="special-requests-input" />
                </div>

                <Button type="submit" disabled={loading} className="w-full btn-primary h-14 text-base" data-testid="submit-booking">
                  {loading ? 'Processing...' : 'Confirm Reservation'}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Info */}
            <div className="space-y-8">
              <div className="card-elegant p-6 bg-gradient-to-br from-white to-malva/5">
                <h3 className="font-serif text-2xl text-gray-800 mb-4">Dining Hours</h3>
                <div className="space-y-3 text-gray-500">
                  <p><span className="text-gray-700 font-medium">Lunch:</span> Monday - Sunday, 12:00 PM - 03:00 PM</p>
                  <p><span className="text-gray-700 font-medium">Dinner:</span> Monday - Sunday, 05:00 PM - 10:30 PM</p>
                  <p className="text-malva-dark">Open 7 Days a Week</p>
                </div>
              </div>

              <div className="card-elegant p-6 bg-gradient-to-br from-white to-silver-bright">
                <h3 className="font-serif text-2xl text-gray-800 mb-4">Reservation Policy</h3>
                <ul className="space-y-3 text-gray-500">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-malva-dark flex-shrink-0 mt-0.5" />
                    <span>Reservations can be made up to all day</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-malva-dark flex-shrink-0 mt-0.5" />
                    <span>Please arrive within 15 minutes of your reservation time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-malva-dark flex-shrink-0 mt-0.5" />
                    <span>Cancellations must be made 24 hours in advance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-malva-dark flex-shrink-0 mt-0.5" />
                    <span>For parties larger than 10 guests, please enquiry at desk</span>
                  </li>
                </ul>
              </div>

              <div className="card-elegant p-6 bg-gradient-to-br from-malva/10 to-white">
                <p className="text-gray-700 mb-2 font-serif text-lg">Private Events</p>
                <p className="text-gray-500 text-sm mb-4">
                  Looking to host a special event? Our private dining room accommodates up to 24 guests.
                </p>
                <a href="/contact" className="text-malva-dark hover:text-malva-darker transition-colors duration-300 text-sm inline-flex items-center gap-1 font-medium">
                  Inquire Now <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
